import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { Grid, Typography } from '@mui/material';
import { registerUserWithMobile, signout, loginWithToken } from 'actions/authActions';
import LeadsMobileVerification from 'views/Auth/SignInOTPVerify/LeadsMobileVerification';
import leadsService from 'services/leadsService';
import RfqUnavailableNotice from 'views/VendorQuotationForms/RfqUnavailableNotice';

const formPaths = {
    material: '/material-form',
    labour: '/labour-form',
    contractor: '/providing-form',
};

function QuotationFormRouter() {
    const { rfqType, rfqId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const params = new URLSearchParams(location.search);
    const mobileNumber = params.get('mobile');
    const userEmail = params.get('email');
    const token = params.get('token');
    const userProfile = useSelector((state) => state.auth.user);
    const userProfileRef = useRef(userProfile);
    userProfileRef.current = userProfile;

    const [openOtpVerifyModal, setOpenOtpVerifyModal] = useState(false);
    const [tokenResult, setTokenResult] = useState(null);
    const [access, setAccess] = useState(null);
    const [retry, setRetry] = useState(0);
    const loginRef = useRef(null);
    const redirectedRef = useRef(null);

    const loginKey = JSON.stringify([token, mobileNumber, userEmail]);
    const tokenReady = !token || (tokenResult?.key === loginKey && tokenResult.status === 'success');
    const userId = userProfile?.userId;
    const vendorsInKey = JSON.stringify(userProfile?.vendorsIn || []);
    const requestKey = JSON.stringify([rfqId, userId, vendorsInKey, loginKey]);
    const accessReady = access?.key === requestKey;
    const rfqData = accessReady && access?.status === 'success' ? access.rfqData : null;

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        let redirectTimeout;

        // Reuse the pending login across effect cleanups instead of cancelling it on auth updates.
        if (loginRef.current?.key !== loginKey) {
            const login = async () => {
                const currentUser = userProfileRef.current;
                if (currentUser) {
                    if (mobileNumber && currentUser.mobile === mobileNumber) return;
                    if (!mobileNumber && userEmail && currentUser.email?.toLowerCase() === userEmail.toLowerCase()) return;
                    if (mobileNumber || userEmail) await dispatch(signout());
                }
                await dispatch(loginWithToken(token));
            };
            loginRef.current = { key: loginKey, promise: login() };
        }

        loginRef.current.promise.then(() => {
            if (!cancelled) setTokenResult({ key: loginKey, status: 'success' });
        }).catch(() => {
            if (cancelled) return;
            setTokenResult({ key: loginKey, status: 'error' });
            redirectTimeout = setTimeout(() => navigate('/'), 1500);
        });

        return () => {
            cancelled = true;
            clearTimeout(redirectTimeout);
        };
    }, [token, mobileNumber, userEmail, loginKey, dispatch, navigate]);

    useEffect(() => {
        if (token || !mobileNumber) return;
        if (userProfile?.mobile && userProfile.mobile !== mobileNumber) {
            dispatch(signout());
            return;
        }
        if (!userProfile) {
            dispatch(registerUserWithMobile('', mobileNumber, () => setOpenOtpVerifyModal(true)));
        }
    }, [token, mobileNumber, userProfile, dispatch]);

    useEffect(() => {
        if (!userId || !tokenReady || openOtpVerifyModal) return;
        if (!token && mobileNumber && userProfileRef.current?.mobile !== mobileNumber) return;
        let cancelled = false;
        setAccess({ key: requestKey, status: 'loading' });

        // The shared leads store may contain another page, filter or a previous user's results.
        leadsService.fetchQuotationLead(JSON.parse(vendorsInKey), userId, rfqId).then((lead) => {
            if (cancelled) return;
            if (!lead || lead.isDeleted === true || lead.status === 'DELETED') {
                setAccess({ key: requestKey, status: 'denied', message: 'You do not have access to this RFQ.' });
                return;
            }
            const activeSuppliers = (lead.suppliers || []).filter((supplier) => supplier?.isDeleted !== true);
            if (!activeSuppliers.length) {
                setAccess({
                    key: requestKey,
                    status: 'denied',
                    message: `You have been removed from this RFQ. Kindly contact ${lead.requesterName || 'the RFQ creator'}.`,
                });
                return;
            }
            setAccess({ key: requestKey, status: 'success', rfqData: { ...lead, suppliers: activeSuppliers } });
        }).catch((error) => {
            if (cancelled) return;
            const status = error?.response?.status;
            const isAccessDenied = [403, 404, 410].includes(status);
            setAccess({
                key: requestKey,
                status: isAccessDenied ? 'denied' : 'error',
                message: isAccessDenied
                    ? error.response.data?.message || 'This invitation link is no longer active.'
                    : 'We could not load your RFQ. Please try again.',
            });
        });

        return () => { cancelled = true; };
    }, [rfqId, userId, vendorsInKey, requestKey, token, tokenReady, mobileNumber, openOtpVerifyModal, retry]);

    useEffect(() => {
        if (!rfqData || !tokenReady || !userId || openOtpVerifyModal || redirectedRef.current === requestKey) return;
        const formPath = formPaths[rfqType];
        if (!formPath) return;
        const currentSupplier = rfqData.suppliers[0];
        const isUpdateRequested = currentSupplier.quotationUpdateRequest?.isRequested === true;
        const readOnly = rfqData.status === 'CLOSED' || (rfqData.status === 'PENDING APPROVAL' && !isUpdateRequested);
        const isEdit = currentSupplier.quotationReceived === true && !readOnly;
        const modeQuery = readOnly ? '&readOnly=true' : isEdit ? '&isEdit=true' : '';
        redirectedRef.current = requestKey;
        navigate(`${formPath}?rfqId=${rfqData.rfqId}${modeQuery}&rfqType=${rfqData.rfqType}`, {
            state: { rfqData, isEdit, supplierId: currentSupplier.supplierId },
        });
    }, [rfqData, rfqType, tokenReady, userId, openOtpVerifyModal, requestKey, navigate]);

    if (token && tokenResult?.key === loginKey && tokenResult.status === 'error') {
        return <Typography align="center">This link has expired or is invalid. You are being redirected...</Typography>;
    }

    if (token && !tokenReady) {
        return (
            <Grid container justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
                <Typography ml={3}>Validating your secure login link, please wait...</Typography>
            </Grid>
        );
    }

    if (openOtpVerifyModal) {
        return (
            <LeadsMobileVerification
                isWhatsappNo={false}
                setOtpPopUp={setOpenOtpVerifyModal}
                setOpenLogInModal={setOpenOtpVerifyModal}
                mobile={mobileNumber}
            />
        );
    }

    if ((!token && mobileNumber && !userProfile) || (userId && (!accessReady || access.status === 'loading'))) {
        return (
            <div className="flex flex-col justify-center items-center h-screen">
                <div className="animate-spin border-2 border-orange-500 h-8 w-8 rounded-full mb-2" />
                <p>{userId ? 'Loading your quotation request form...' : 'Preparing secure login...'}</p>
            </div>
        );
    }

    if (accessReady && access.status === 'error') {
        return (
            <div className="flex flex-col justify-center items-center h-screen text-center px-4">
                <p>{access.message}</p>
                <button type="button" className="mt-4 text-blue-600 underline" onClick={() => setRetry((value) => value + 1)}>
                    Try again
                </button>
            </div>
        );
    }

    if (!rfqData) {
        return <RfqUnavailableNotice title="Quotation access unavailable" message={accessReady ? access.message : 'You do not have access to this RFQ. Contact: 9373759782 | support@neevay.com'} />;
    }

    if (!formPaths[rfqType]) return <Typography align="center">Invalid RFQ type.</Typography>;
    return null;
}

export default QuotationFormRouter;
